<script setup>
import { ref, onMounted } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Tag from "primevue/tag";
import { useToast } from "primevue/usetoast";
import { dashboardAPI } from "../service/api.js";

const toast = useToast();
const mappings = ref([]);
const loading = ref(false);
const resolvingId = ref(null);

const sourceName = (row) =>
  row.source_company_name || [row.source_first_name, row.source_last_name].filter(Boolean).join(" ") || "—";

const fetchMappings = async () => {
  loading.value = true;
  try {
    const response = await dashboardAPI.getVendorSourceMappings({ status: "duplicate_suspected" });
    mappings.value = response.data || [];
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Failed to load review queue",
      detail: err.response?.data || err.message || "Failed to load",
      life: 5000,
    });
  } finally {
    loading.value = false;
  }
};

const resolve = async (row, action) => {
  resolvingId.value = row.mapping_id;
  try {
    await dashboardAPI.resolveVendorSourceMapping(row.mapping_id, { action });
    toast.add({
      severity: "success",
      summary: action === "link_existing" ? "Linked to existing vendor" : "Created as new vendor",
      detail:
        action === "create_new"
          ? "New vendor staged — approve it in the Vendors tab to enable payments."
          : "If the linked vendor is already digital-enabled, held payments have been requeued.",
      life: 5000,
    });
    await fetchMappings();
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Resolve Failed",
      detail: err.response?.data || err.message || "Failed to resolve mapping",
      life: 5000,
    });
  } finally {
    resolvingId.value = null;
  }
};

onMounted(fetchMappings);
</script>

<template>
  <div>
    <p class="hint">
      A newly-sighted Terra payee's name closely matched an existing vendor, so CarePay held it
      here instead of silently creating a possible duplicate business record. Link it to the
      existing vendor below, or confirm it's genuinely a different one.
    </p>
    <DataTable :value="mappings" :loading="loading" dataKey="mapping_id">
      <template #empty>Nothing pending review.</template>
      <Column header="New Sighting">
        <template #body="slotProps">
          <div>{{ sourceName(slotProps.data) }}</div>
          <div class="tax-id">Tax ID: {{ slotProps.data.source_tax_id }}</div>
        </template>
      </Column>
      <Column header="Possible Match">
        <template #body="slotProps">
          <div v-if="slotProps.data.candidate_name">{{ slotProps.data.candidate_name }}</div>
          <div v-if="slotProps.data.candidate_tax_id" class="tax-id">
            Tax ID: {{ slotProps.data.candidate_tax_id }}
          </div>
          <span v-if="!slotProps.data.candidate_name">—</span>
        </template>
      </Column>
      <Column header="Confidence">
        <template #body="slotProps">
          <Tag v-if="slotProps.data.match_confidence != null" :value="`${Math.round(slotProps.data.match_confidence)}%`" />
        </template>
      </Column>
      <Column field="created_at" header="Sighted" />
      <Column header="Actions" :exportable="false" style="min-width: 20rem">
        <template #body="slotProps">
          <Button
            label="Link to this vendor"
            size="small"
            :disabled="!slotProps.data.candidate_vendor_id"
            :loading="resolvingId === slotProps.data.mapping_id"
            @click="resolve(slotProps.data, 'link_existing')"
          />
          <Button
            label="Not a duplicate — create new"
            size="small"
            severity="secondary"
            outlined
            :loading="resolvingId === slotProps.data.mapping_id"
            @click="resolve(slotProps.data, 'create_new')"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped>
.hint {
  color: var(--p-text-muted-color, #666);
  margin-bottom: 1rem;
  max-width: 60rem;
}
.tax-id {
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #666);
}
</style>
